import { makeVar, ReactiveVar, useReactiveVar } from '@apollo/client';

import { AlertModalProps } from '../organisms';

type AlertModalOptions = Omit<AlertModalProps, 'isOpen'>;
type MaybeAlertModalOptions = AlertModalOptions | null;

export type AlertModalVar = ReactiveVar<MaybeAlertModalOptions>;

// Can and should only display one alert modal at a time
const alertModalVar: AlertModalVar = makeVar<MaybeAlertModalOptions>(null);

export const useAlertModal = (): MaybeAlertModalOptions => useReactiveVar(alertModalVar);

export enum AlertModalId {
  GAAP_PROJECT_DETAILS_UNSAVED_CHANGES = 'GAAP_PROJECT_DETAILS_UNSAVED_CHANGES',
}

const alertModalOptionsMap = new Map<AlertModalId, Pick<AlertModalOptions, 'onConfirm' | 'onCancel'>>();

/**
 * Specify the options of the alert modal that MAY eventually be shown. This function does NOT show the alert modal.
 * The event that triggers the configuration for a given alert modal may be separate from the event that triggers the
 * visibility of the alert modal. These two events are deliberately decoupled. This allows us to trigger the showing of
 * an alert modal from multiple places while the configuration of the alert modal is typically only done once from
 * within the component that can declare what information the alert modal should display.
 */
export const addAlertModalOptions = (
  alertModalId: AlertModalId,
  alertModalOptions: Pick<AlertModalOptions, 'onConfirm' | 'onCancel'> = {},
): void => {
  alertModalOptionsMap.set(alertModalId, alertModalOptions);
};

export const removeAlertModalOptions = (alertModalId: AlertModalId): boolean =>
  alertModalOptionsMap.delete(alertModalId);

export const shouldShowAlertModal = (alertModalId: AlertModalId): boolean =>
  alertModalOptionsMap.has(alertModalId) && alertModalVar() === null;

/**
 * Shows an alert modal that has already been configured.Before showing an alert modal, it's usually best to check if it
 * should be shown via `shouldShowAlertModal`.
 */
export const showAlertModal = (alertModalId: AlertModalId, alertModalOptions: AlertModalOptions): void => {
  // Set the alert modal options for the desired alert when the alert is shown
  alertModalVar({
    onCancel: (): void => {
      hideAlertModal();
    },
    ...alertModalOptionsMap.get(alertModalId),
    ...alertModalOptions,
  });
};

export const hideAlertModal = (): void => {
  alertModalVar(null);
};
